export const searchReferenceNumbers = async (
  apiBaseUrl,
  callProtectedApi,
  { search, page, size, historical = true } = {},
) => {
  const query = String(search ?? "").trim();
  if (!apiBaseUrl || !query) return { items: [], total: 0, success: false };

  const url =
    `${apiBaseUrl}/api/v1/hierarchy/documents/referenceNumber` +
    `?search=${encodeURIComponent(query)}` +
    `${page != null ? `&page=${encodeURIComponent(Number(page))}` : ""}` +
    `${size != null ? `&size=${encodeURIComponent(Number(size))}` : ""}` +
    `&historical=${encodeURIComponent(Boolean(historical))}`;

  const res = await callProtectedApi(url, {
    headers: { "Content-Type": "application/json" },
  });

  return {
    items: Array.isArray(res?.data?.items) ? res.data.items : [],
    total: Number(res?.data?.total) || 0,
    success: Boolean(res?.data?.success),
  };
};

export const DUPLICATE_ISSUE_MESSAGE =
  "This issue number already exists for this reference number. Please enter a different issue number.";

const normalizeComparableText = (value) =>
  String(value ?? "").trim().toLowerCase();

const normalizeIssueKey = (value) => {
  const normalized = normalizeComparableText(value);
  if (!normalized) return "";

  if (/^[+-]?\d+(?:\.\d+)?$/.test(normalized)) {
    const numeric = Number(normalized);
    if (Number.isFinite(numeric)) return `number:${numeric}`;
  }

  return `text:${normalized}`;
};

const parseNumericIssue = (value) => {
  const normalized = String(value ?? "").trim();
  if (!/^\d+$/.test(normalized)) return null;

  const numericIssue = Number(normalized);
  return Number.isSafeInteger(numericIssue) ? numericIssue : null;
};

export const getNextIssueNumberForReference = async (
  apiBaseUrl,
  callProtectedApi,
  { referenceNumber, sourceIssue } = {},
) => {
  const normalizedReferenceNumber = normalizeComparableText(referenceNumber);
  if (!apiBaseUrl || !normalizedReferenceNumber) return "";

  const pageSize = 200;
  const items = [];
  let page = 1;
  let totalPages = 1;

  do {
    const response = await searchReferenceNumbers(
      apiBaseUrl,
      callProtectedApi,
      {
        search: String(referenceNumber).trim(),
        page,
        size: pageSize,
        historical: true,
      },
    );
    const pageItems = Array.isArray(response.items) ? response.items : [];
    items.push(...pageItems);
    totalPages = Math.max(
      1,
      Math.ceil((Number(response.total) || pageItems.length) / pageSize),
    );
    page += 1;
  } while (page <= totalPages);

  const numericIssues = items
    .filter(
      (item) =>
        normalizeComparableText(item?.referenceNumber) ===
        normalizedReferenceNumber,
    )
    .map((item) => parseNumericIssue(item?.issue))
    .filter((issue) => issue !== null);
  const numericSourceIssue = parseNumericIssue(sourceIssue);

  if (numericSourceIssue !== null) numericIssues.push(numericSourceIssue);
  if (!numericIssues.length) return "";

  return String(Math.max(...numericIssues) + 1);
};

export const findDuplicateStandardIssue = async (
  apiBaseUrl,
  callProtectedApi,
  {
    referenceNumber,
    issue,
    excludeStandardId = null,
  } = {},
) => {
  const normalizedReferenceNumber = normalizeComparableText(referenceNumber);
  const normalizedIssue = normalizeIssueKey(issue);
  if (!apiBaseUrl || !normalizedReferenceNumber || !normalizedIssue) return null;

  const response = await searchReferenceNumbers(apiBaseUrl, callProtectedApi, {
    search: String(referenceNumber).trim(),
    size: 100,
    historical: true,
  });
  const excludedId = String(excludeStandardId ?? "").trim();

  return (
    response.items.find((item) => {
      const itemId = String(
        item?.standardId ?? item?.id ?? item?.summary?.standardId ?? "",
      ).trim();

      return (
        normalizeComparableText(item?.referenceNumber) ===
          normalizedReferenceNumber &&
        normalizeIssueKey(item?.issue) === normalizedIssue &&
        (!excludedId || itemId !== excludedId)
      );
    }) ?? null
  );
};

const resolvedReferenceNumberCache = new Map();
const pendingReferenceNumberRequests = new Map();

const buildReferenceCacheKey = (apiBaseUrl, standardId) =>
  `${String(apiBaseUrl ?? "").trim()}::${String(standardId ?? "").trim()}`;

const readCachedReferenceNumber = (apiBaseUrl, standardId) =>
  resolvedReferenceNumberCache.get(buildReferenceCacheKey(apiBaseUrl, standardId)) ??
  "";

const cacheResolvedReferenceNumber = (apiBaseUrl, standardId, referenceNumber) => {
  const cacheKey = buildReferenceCacheKey(apiBaseUrl, standardId);
  const normalizedReferenceNumber = String(referenceNumber ?? "").trim();
  if (!cacheKey || !normalizedReferenceNumber) return;
  resolvedReferenceNumberCache.set(cacheKey, normalizedReferenceNumber);
};

const resolveReferenceNumberById = async (
  apiBaseUrl,
  callProtectedApi,
  standardId,
) => {
  const cacheKey = buildReferenceCacheKey(apiBaseUrl, standardId);
  const cachedReferenceNumber = readCachedReferenceNumber(apiBaseUrl, standardId);
  if (cachedReferenceNumber) {
    return cachedReferenceNumber;
  }

  const pendingRequest = pendingReferenceNumberRequests.get(cacheKey);
  if (pendingRequest) {
    return pendingRequest;
  }

  const request = (async () => {
    try {
      const url = `${apiBaseUrl}/api/v1/hierarchy/document?id=${encodeURIComponent(standardId)}`;
      const res = await callProtectedApi(url, {
        headers: { "Content-Type": "application/json" },
      });
      const refNo =
        res?.data?.summary?.referenceNumber ??
        res?.data?.referenceNumber ??
        "";
      if (refNo) {
        cacheResolvedReferenceNumber(apiBaseUrl, standardId, refNo);
      }
      return String(refNo ?? "").trim();
    } finally {
      pendingReferenceNumberRequests.delete(cacheKey);
    }
  })();

  pendingReferenceNumberRequests.set(cacheKey, request);
  return request;
};

export const __resetReferenceNumberResolutionCache = () => {
  resolvedReferenceNumberCache.clear();
  pendingReferenceNumberRequests.clear();
};

/**
 * Resolves human-readable reference numbers for a list of standardIds.
 * Uses GET /api/v1/hierarchy/document?id={id} — the same endpoint used by
 * useHierarchyApi.getDocument — which reliably returns summary.referenceNumber.
 *
 * @param {string} apiBaseUrl
 * @param {Function} callProtectedApi
 * @param {number[]} standardIds
 * @returns {Promise<Map<number, string>>} standardId → referenceNumber
 */
export const resolveReferenceNumbers = async (
  apiBaseUrl,
  callProtectedApi,
  standardIds,
) => {
  const result = new Map();
  if (!apiBaseUrl || !Array.isArray(standardIds) || standardIds.length === 0) {
    return result;
  }

  const unique = [...new Set(standardIds.filter((id) => id != null))];

  await Promise.allSettled(
    unique.map(async (standardId) => {
      try {
        const refNo = await resolveReferenceNumberById(
          apiBaseUrl,
          callProtectedApi,
          standardId,
        );
        if (refNo) {
          result.set(Number(standardId), String(refNo));
        }
      } catch {
        // Leave this ID unresolved; the chip will keep its fallback value.
      }
    }),
  );

  return result;
};

export const getDocumentForEdit = async (
  apiBaseUrl,
  callProtectedApi,
  standardId,
) => {
  if (!apiBaseUrl || !standardId) return null;

  const url =
    `${apiBaseUrl}/api/v1/hierarchy/documents/${encodeURIComponent(standardId)}` +
    `?action=edit`;

  const res = await callProtectedApi(url, {
    headers: { "Content-Type": "application/json" },
  });

  return res?.data ?? null;
};

export const createStandard = async (
  apiBaseUrl,
  callProtectedApi,
  payload,
) => {
  if (!apiBaseUrl || !payload) return null;

  const url = `${apiBaseUrl}/api/v1/hierarchy/documents`;
  const res = await callProtectedApi(url, {
    method: "POST",
    data: payload,
    headers: { "Content-Type": "application/json" },
  });

  return res?.data ?? null;
};

export const updateStandard = async (
  apiBaseUrl,
  callProtectedApi,
  standardId,
  payload,
) => {
  if (!apiBaseUrl || !standardId || !payload) return null;

  const url = `${apiBaseUrl}/api/v1/hierarchy/documents/${encodeURIComponent(standardId)}`;
  const res = await callProtectedApi(url, {
    method: "PATCH",
    data: payload,
    headers: { "Content-Type": "application/json" },
  });

  return res?.data ?? null;
};

export const moveStandard = async (
  apiBaseUrl,
  callProtectedApi,
  standardId,
  folderId,
) => {
  if (!apiBaseUrl || !standardId || !folderId) return null;

  const url =
    `${apiBaseUrl}/api/v1/hierarchy/documents/${encodeURIComponent(standardId)}` +
    `/move`;
  await callProtectedApi(url, {
    method: "POST",
    data: {
      folderId: Number(folderId),
    },
    headers: { "Content-Type": "application/json" },
  });

  return true;
};

export const deleteStandard = async (
  apiBaseUrl,
  callProtectedApi,
  standardId,
) => {
  if (!apiBaseUrl || !standardId) return null;

  const url = `${apiBaseUrl}/api/v1/hierarchy/documents/${encodeURIComponent(standardId)}`;
  const res = await callProtectedApi(url, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
  });

  return res?.data ?? null;
};

export const createNewIssue = async (
  apiBaseUrl,
  callProtectedApi,
  standardId,
) => {
  if (!apiBaseUrl || !standardId) return null;

  const url =
    `${apiBaseUrl}/api/v1/hierarchy/documents` +
    `?newIssueFrom=${encodeURIComponent(standardId)}`;
  const res = await callProtectedApi(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });

  return res?.data ?? null;
};
